/**
 * B1022 d.2 — the public CHECK A BALANCE page (845:2), laid out from the
 * item's words (the PNG is unreachable from the building seat; redline
 * pending — the design side measures it): the Gift cards bar without a cart,
 * the hero, the form (card number, PIN with its ONE generic note, the one
 * door CHECK BALANCE), and the answer: the card's face, AVAILABLE NOW $x,
 * "of $y loaded · •••• last4", a status note when it can't be used, HISTORY
 * rows and USE IT ONLINE →. No sign-in; nothing here holds what was typed.
 * The shapes come from lib/giftCardBalance.ts; this file only lays them out.
 */
import type { ReactElement } from "react";
import { Banner, Button, Input } from "@transact/ui";
import { PIN_NOTE, type BalanceFacts, type BalanceIssues } from "../lib/giftCardBalance.js";
import { Bar, CardFace, type GiftCardsVenue } from "./giftCardsPage.js";

export function GiftCardBalancePage(props: {
  readonly venue: GiftCardsVenue;
  readonly ticketsUrl?: string | undefined;
  readonly number: string;
  readonly pin: string;
  readonly issues: BalanceIssues;
  readonly onNumber: (v: string) => void;
  readonly onPin: (v: string) => void;
  readonly onCheck: () => void;
  readonly busy: boolean;
  /** The door's own refusal (no match, locked, replaced, too many checks) — one sentence. */
  readonly refusal?: string | undefined;
  readonly result?: BalanceFacts | undefined;
}): ReactElement {
  const r = props.result;
  return (
    <main className="gcards gcbal" aria-label="Gift card balance">
      <Bar venue={props.venue} ticketsUrl={props.ticketsUrl} />
      <div className="gcards-body">
        <div className="gcards-hero">
          <p className="gcards-kicker">Gift cards</p>
          <h1 className="gcards-title">Check a gift card balance</h1>
          <p className="gcards-sub">Enter the number and PIN from the gift card email — no account needed.</p>
        </div>
        <div className="gcbal-grid">
          <form
            className="gcards-card gcbal-form"
            noValidate
            aria-label="Check a balance"
            data-redline="gcbal-form"
            onSubmit={(e) => {
              e.preventDefault();
              props.onCheck();
            }}
          >
            <Input
              id="gcbal-number"
              required
              width="md"
              label="Card number"
              value={props.number}
              inputMode="numeric"
              autoComplete="off"
              placeholder="0000 0000 0000 0000"
              hint="16 digits, from the gift card email."
              error={props.issues.number}
              onChange={props.onNumber}
            />
            <Input id="gcbal-pin" required width="sm" label="PIN" value={props.pin} inputMode="numeric" type="password" autoComplete="off" hint={PIN_NOTE} error={props.issues.pin} onChange={props.onPin} />
            {props.refusal !== undefined ? <Banner tone="danger">{props.refusal}</Banner> : null} {/* wayback-ok: the number and PIN fields above it and CHECK BALANCE below it are the way back — the banner clears on the next check */}
            <Button type="submit" variant="primary" size="lg" busy={props.busy} onClick={() => undefined /* dead-ok: a submit button — the form's onSubmit is the act */}>
              Check balance
            </Button>
          </form>
          {r !== undefined ? (
            <section className="gcards-card gcbal-result" aria-labelledby="gcbal-result-head" data-redline="gcbal-result">
              <CardFace businessName={props.venue.businessName} amount={r.available} tone={0} size="preview" />
              <h2 className="gcards-cap" id="gcbal-result-head">
                Available now
              </h2>
              <p className="gcbal-available">{r.available}</p>
              <p className="gcbal-loaded">{r.ofLoaded}</p>
              {r.note !== undefined ? <Banner tone="info">{r.note}</Banner> : null}
              {r.history.length > 0 ? (
                <>
                  <h3 className="gcards-cap">History</h3>
                  <ul className="gcbal-history">
                    {r.history.map((h, i) => (
                      <li key={i} className="gcbal-row">
                        <span className="gcbal-row-kind">{h.kind}</span>
                        <span className="gcbal-row-when">{h.when}</span>
                        <span className="gcbal-row-amount">{h.amount}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {r.usable && props.ticketsUrl !== undefined ? (
                /* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */
                <Button variant="primary" href={props.ticketsUrl} onClick={() => undefined}>
                  Use it online →
                </Button>
              ) : null}
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}
